import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { UIEvent } from 'react'

import { DateService } from 'common/services/dateService'

import { useLazyGetOlderTeamMessagesQuery } from 'features/TeamChat/api/teamChat.api'
import type { TeamMember, TeamMessage } from 'features/TeamChat/api/teamChat.types'
import {
  LOAD_OLDER_OFFSET_PX,
  NEAR_BOTTOM_PX,
  TEAM_COPY,
} from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'
import { dayLabel, formatTeamTime, siteDayKey } from 'features/TeamChat/teamChat.time'

import { MessageItem } from './MessageItem'

export type MessagesStatus = 'loading' | 'error' | 'ready'
type Props = {
  messages: TeamMessage[]
  status: MessagesStatus
  hasMore: boolean
  members: TeamMember[]
  timeZone: string
}

type Anchor = { firstId: number; height: number }

// Nothing while loading, an error line on failure, the empty text only after a successful empty load.
const statusText = (status: MessagesStatus, count: number): string | null => {
  if (status === 'error') return TEAM_COPY.LOAD_FAILED
  return status === 'ready' && count === 0 ? TEAM_COPY.NO_MESSAGES : null
}

// My confirmed copy replaces a bubble that already slid in, so only the pending one animates.
const isArrival = (m: TeamMessage, settledId: number | null) =>
  settledId !== null && m.id > settledId && (!m.is_mine || Boolean(m.pending))

export const MessageList = ({ messages, status, hasMore, members, timeZone }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const anchor = useRef<Anchor | null>(null)
  const nearBottom = useRef(true)
  const [loadOlder, { isFetching }] = useLazyGetOlderTeamMessagesQuery()
  const firstId = messages[0]?.id
  const last = messages.at(-1)
  const nowIso = DateService.getCurrentTimeInTimezone(timeZone).toISOString()
  // Zone conversions are the costly part, so each message is converted once per list change.
  const stamps = useMemo(
    () =>
      messages.map((m) => ({
        dayKey: siteDayKey(m.created_at, timeZone),
        time: formatTeamTime(m.created_at, timeZone),
      })),
    [messages, timeZone],
  )
  const placeholder = statusText(status, messages.length)
  // Only messages that arrive after the first load animate in; history and older pages do not.
  const [settledId, setSettledId] = useState<number | null>(null)
  if (settledId === null && status === 'ready') setSettledId(last?.id ?? 0)

  // Keep the reading position only for a real prepend; any other change drops the anchor.
  useLayoutEffect(() => {
    const el = scrollRef.current
    const a = anchor.current
    anchor.current = null
    if (!el || !a || firstId === a.firstId || !messages.some((m) => m.id === a.firstId)) return
    el.scrollTop += el.scrollHeight - a.height
  }, [firstId])

  // Follow new messages only for a reader at the bottom or for their own message.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && (nearBottom.current || last?.is_mine)) el.scrollTop = el.scrollHeight
  }, [last?.id])

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    nearBottom.current = scrollHeight - scrollTop - clientHeight <= NEAR_BOTTOM_PX
    if (scrollTop > LOAD_OLDER_OFFSET_PX || !hasMore || isFetching || !firstId) return
    anchor.current = { firstId, height: scrollHeight }
    loadOlder({ before: firstId })
  }

  return (
    // aria-busy keeps screen readers from reading a prepended older page aloud.
    <div
      ref={scrollRef}
      className={`${styles.scroll} ${styles.chatScroll}`}
      role='log'
      aria-live='polite'
      aria-busy={isFetching}
      onScroll={onScroll}>
      {placeholder && <div className={styles.empty}>{placeholder}</div>}
      {messages.map((m, i) => (
        <Fragment key={m.id}>
          {(i === 0 || stamps[i - 1].dayKey !== stamps[i].dayKey) && (
            <div className={styles.day}>{dayLabel(m.created_at, timeZone, nowIso)}</div>
          )}
          <MessageItem
            message={m}
            members={members}
            time={stamps[i].time}
            enter={isArrival(m, settledId)}
          />
        </Fragment>
      ))}
    </div>
  )
}
